import { observable } from "mobx";

import {
  apiGetUsers,
  apiGetUsersMonitoringUsers,
  apiGetUsersPhysicians,
  FeatureFlagUpdate,
  FullUserResponse,
  UserFeatureFlag,
  UserResponse,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { putUserFeatureFlag } from "../../../api/user-feature-flags";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { Merge } from "../../../utils/types";

export type UsersItem = Merge<FullUserResponse | UserResponse>;

/**
 * Shape of an in-progress user edit. Mirrors {@link UsersItem} except
 * `visible_patients`, which {@link Users.prepareDraft} normalizes from the
 * API's patient objects down to a plain list of ids.
 */
export type UserEditDraft = Omit<Partial<UsersItem>, "visible_patients"> & {
  visible_patients?: number[];
};

export class Users extends DataModel<UsersItem> {
  readonly idAttr = "uuid";
  @observable accessor userMapping: Record<string, string> = {};
  @observable accessor loading = false;

  constructor(public api: ApiConnector) {
    super([], api.permissions, api.requireUserUUID());
    this.defaultFilter = {
      role: [
        "admin",
        "seerlinq-user",
        "study-physician",
        "physician",
        "patient",
      ],
    };

    // access
    this.access = {
      editRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  dataInit() {
    super.init();
    this.getMapping();
  }

  async init() {
    await this.load(async () => (await apiGetUsers()).data.users);
  }

  async initPhysiciansOnly() {
    await this.load(async () => (await apiGetUsersPhysicians()).data.users);
  }

  async initMonitoringUsers(country?: string, region?: number) {
    await this.load(
      async () =>
        (await apiGetUsersMonitoringUsers({ query: { country, region } })).data
          .users,
    );
  }

  private async load(fetch: () => Promise<UsersItem[]>) {
    this.loading = true;
    try {
      this.data = await fetch();
      this.dataInit();
    } finally {
      this.loading = false;
    }
  }

  getMapping() {
    for (const user of this.data) {
      this.userMapping[user.uuid] = user.username;
    }
  }

  /**
   * Deliberately not part of the edit dialog's form patch: that patch is a
   * shallow per-key diff, so a `feature_flags` key in it would send the whole
   * merged registry - the one thing {@link putUserFeatureFlag} exists to avoid.
   *
   * The list item is patched in place from the response, so there is no
   * refetch and no `updateUser` page reload between toggles.
   */
  async updateFeatureFlag(
    uuid: string,
    flag: UserFeatureFlag,
    update: FeatureFlagUpdate,
  ) {
    const flags = await putUserFeatureFlag(uuid, flag, update);
    const user = this.data.find((item) => item.uuid === uuid);
    if (user) {
      user.feature_flags = flags;
    }
  }

  prepareDraft(item: UsersItem): UserEditDraft {
    return {
      ...super.prepareDraft(item),
      visible_patients: (item.visible_patients ?? []).map((p) => p.patient_id),
    };
  }
}
